<template>
	<div
		v-if="similarRelations && (similarRelations.Items?.length ?? 0) > 0"
		class="flex gap-2 items-center flex-col"
	>
		<component :is="titleTag" class="section-title mb-6" :class="titleClass">
			{{ similarRelations.Name || $t("similar_products") }}
		</component>
		<Carousel
			:slides="similarRelations.Items || []"
			:type-of-slide="ItemType.PRODUCT"
			:blok="{ number_of_slides_to_show: '4', show_arrows: true }"
			:show-add-to-cart="showAddToCart"
			:aligning="showAddToCart ? 'items-stretch' : undefined"
		/>
	</div>
</template>

<script setup lang="ts">
import type { SimplifiedProductRelations } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { ref } from "vue";
import Carousel from "~/layers/base/storyblok/Carousel/Carousel.vue";
import { ItemType, ProductPrefix } from "~/utils/constants";
import {
	hasDedicatedBlock,
	matchesRelationPrefix
} from "~/utils/product/relations";

interface Props {
	/**
	 * Quick add on each card. Also switches the track to `items-stretch`:
	 * centred slides break the card's `h-full` + `mt-auto`.
	 */
	showAddToCart?: boolean;
	/**
	 * Relation groups this page renders in a block of its own. Per page, not
	 * global: a template without that block needs this carousel to keep
	 * rendering the group, or the products vanish from the PDP.
	 */
	excludeRelations?: ProductPrefix[];
}

// See Carousel.vue: `undefined` rather than Vue's Boolean cast to `false`.
const { showAddToCart = undefined, excludeRelations = [] } =
	defineProps<Props>();

const { relations } = storeToRefs(useProductStore());
const { titleTag, titleClass } = useProductRecommendationsConfig();

const similarRelations = ref<SimplifiedProductRelations | null>(null);

const currentRelations = computed(() => {
	const productRelations = relations.value as SimplifiedProductRelations[];
	const similarProducts = productRelations.find((relation) =>
		relation.Code?.includes(ProductPrefix.SIMILAR_PRODUCTS)
	);

	if (similarProducts) {
		return similarProducts;
	}

	// Skip groups that already have their own block on the page.
	return (
		productRelations.find(
			(relation) =>
				!hasDedicatedBlock(relation) &&
				!excludeRelations.some((prefix) =>
					matchesRelationPrefix(relation.Code, prefix)
				)
		) || null
	);
});

watch(
	currentRelations,
	(newRelations: SimplifiedProductRelations | null) => {
		similarRelations.value = newRelations;
	},
	{ immediate: true }
);
</script>
